import {useEffect, useRef} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useUrlUpdater} from 'components/data/WithUrlUpdater'
import {
  getAccommodationPageHotelId,
  getRoomsActiveFilters
} from 'modules/accommodation/selectors'
import {setRoomsFiltersLoading} from 'modules/accommodation/slice'
import {getOverlay} from 'modules/overlay/selectors'
import {getHotelApiRoomsParameters} from 'modules/searchApi/selectors'

import {useRouterProvider} from '@vio/core/src/_web/router/context/RouterContext'
import {getUrlParams} from '@vio/core/src/_web/utils/url'
import {filtersToCacheKey} from '@vio/core/src/room/business/utils/filters'
import {RoomsFilterKey} from '@vio/core/src/room/business/utils/filters'
import {
  roomSearchStarted,
  roomSearchSucceeded
} from '@vio/core/src/sapi/services/searchApi/action'

export const useHotelRoomsFilters = () => {
  const dispatch = useDispatch()
  const overlayState = useSelector(getOverlay)
  const activeRoomsFilters = useSelector(getRoomsActiveFilters)
  const hotelId = useSelector(getAccommodationPageHotelId)
  const params = useSelector(state =>
    getHotelApiRoomsParameters(state, hotelId)
  )
  const {updateLocation} = useUrlUpdater()
  const {pathname} = useRouterProvider()
  const changeListenerRef = useRef<() => void>()

  /**
   * Set (or remove) multiple filters
   */
  const setRoomsFilters = (filters: RoomsFilterKey[]) => {
    const urlParams = getUrlParams()
    const newParams = {
      ...urlParams,
      roomFilters: filters.join(','),
      roomUserSearch: '1'
    }
    if (!filters.length) delete newParams.roomFilters

    updateLocation(newParams, {
      basePath: pathname,
      method: 'replace',
      mergeWithCurrentParams: false,
      shallow: true,
      state: {overlay: overlayState}
    })

    // Filtering happens client side, so fire mock API request events once the change comes through the navigation + state change
    changeListenerRef.current = () => {
      dispatch(roomSearchStarted({}))
      dispatch(roomSearchSucceeded({params}))
    }

    // And fake loading state to give user UI feedback for the action
    dispatch(setRoomsFiltersLoading(true))
    setTimeout(() => {
      dispatch(setRoomsFiltersLoading(false))
    }, 300)
  }

  // Whenever filters state changes, trigger the listener from the last action
  const filtersCacheKey = filtersToCacheKey(activeRoomsFilters)
  useEffect(() => {
    changeListenerRef.current?.()
    changeListenerRef.current = undefined
  }, [filtersCacheKey])

  /**
   * Toggle a single filter
   */
  const toggleRoomsFilter = (filterId: RoomsFilterKey) => {
    const newFilters = new Set(activeRoomsFilters)
    newFilters.delete(filterId) || newFilters.add(filterId)

    setRoomsFilters(Array.from(newFilters))
  }

  return {
    activeRoomsFilters,
    setRoomsFilters,
    toggleRoomsFilter
  }
}
